import React, { createContext, useContext, useState, useEffect } from 'react';
import BrandingSettingsService from 'services/BrandingSettingsService';
import FileUploadService from 'services/FileUploadService';
import env from '../../../../package.json';
import BrandingLoader from './BrandingLoader';

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [brandingSettings, setBrandingSettings] = useState({
    // Основные цвета
    primary_color: '#4783ed',
    secondary_color: '#81a8ec',

    // Цвета текста
    text_color: '#333333',
    text_light_color: '#666666',
    text_dark_color: '#000000',
    text_on_primary: '#ffffff',
    text_active: '#ffffff',
    text_inactive: '#ffffff',

    // Цвета текста для разных областей
    text_sidebar: '#ffffff',
    text_footer: '#666666',
    text_header: '#ffffff',
    text_main: '#333333',

    // Цвета фона
    background_color: '#f8f9fa',
    background_light: '#ffffff',
    background_dark: '#f0f0f0',
    background_hover: '#e9ecef',

    // Цвета обводок/границ
    border_color: '#dee2e6',
    border_light: '#e9ecef',
    border_dark: '#adb5bd',
    border_focus: '#4783ed',

    // Цвета состояний
    active_color: '#ffffff',
    hover_color: '#81a8ec',
    disabled_color: '#6c757d',
    disabled_background: '#e9ecef',

    // Цвета уведомлений
    success_color: '#28a745',
    success_background: '#d4edda',
    error_color: '#dc3545',
    error_background: '#f8d7da',
    warning_color: '#ffc107',
    warning_background: '#fff3cd',
    info_color: '#17a2b8',
    info_background: '#d1ecf1',

    // Цвета специальных элементов
    link_color: '#4783ed',
    link_hover_color: '#81a8ec',
    shadow_color: 'rgba(0,0,0,0.1)',

    // Цвета бургер-меню
    burger_background: '#ffffff',
    burger_color: '#4783ed',
    burger_hover_background: '#f8f9fa',
    burger_hover_color: '#81a8ec',
    burger_active_background: '#ffffff',
    burger_active_color: '#000000',

    // Цвета полей ввода
    input_background: '#ffffff',
    input_text: '#333333',
    input_placeholder: '#999999',
    input_border: '#dee2e6',
    input_focus_border: '#4783ed',
    input_focus_background: '#ffffff',

    logo_url: require('assets/icon/logo.png'),
    is_active: false
  });
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadBrandingSettings = async () => {
      try {
        const settings = await BrandingSettingsService.getActiveSettings();

        // Проверяем, что settings не пустой
        if (settings) {
          // Если есть путь к логотипу, формируем полный URL
          if (settings.logo_url && settings.logo_url.startsWith('/files/')) {
            settings.logo_url = `${env.nodeHost}${settings.logo_url}`;
          } else if (!settings.logo_url) {
            // Загружаем логотип через публичный API только если его еще нет
            try {
              const logoData = await FileUploadService.getLogo();

              if (logoData.success && logoData.files && logoData.files.length > 0) {
                const logoFile = logoData.files[0];
                const logoUrl = `${env.nodeHost}${logoFile.file_path}`;
                settings.logo_url = logoUrl;
              } else {
                settings.logo_url = require('assets/icon/logo.png');
              }
            } catch (logoError) {
              console.warn('Ошибка загрузки логотипа:', logoError);
              // Используем дефолтный логотип
              settings.logo_url = require('assets/icon/logo.png');
            }
          }

          setBrandingSettings(settings);
          applyBrandingSettings(settings);
        } else {
          // Если settings пустой, используем дефолтные настройки
          applyBrandingSettings(brandingSettings);
        }
      } catch (error) {
        console.error('Ошибка загрузки настроек брендинга:', error);
        // При ошибке используем дефолтные настройки
        applyBrandingSettings(brandingSettings);
      } finally {
        // Всегда скрываем лоадер после загрузки или ошибки
        setIsLoading(false);
      }
    };

    loadBrandingSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyBrandingSettings = (settings) => {
    const root = document.documentElement;

    if (settings && settings.is_active) {
      // Применяем настройки только если они активны
      // Основные цвета
      if (settings.primary_color) {
        root.style.setProperty('--main-color', settings.primary_color);
        root.style.setProperty('--main-color-border', settings.primary_color);
      }
      if (settings.secondary_color) {
        root.style.setProperty('--secondary-color', settings.secondary_color);
      }

      // Цвета текста
      if (settings.text_color) root.style.setProperty('--text-color', settings.text_color);
      if (settings.text_light_color) root.style.setProperty('--text-light-color', settings.text_light_color);
      if (settings.text_dark_color) root.style.setProperty('--text-dark-color', settings.text_dark_color);
      if (settings.text_on_primary) root.style.setProperty('--text-on-primary', settings.text_on_primary);
      if (settings.text_active) root.style.setProperty('--text-active', settings.text_active);
      if (settings.text_inactive) root.style.setProperty('--text-inactive', settings.text_inactive);

      // Цвета текста для разных областей
      if (settings.text_sidebar) root.style.setProperty('--text-sidebar', settings.text_sidebar);
      if (settings.text_footer) root.style.setProperty('--text-footer', settings.text_footer);
      if (settings.text_header) root.style.setProperty('--text-header', settings.text_header);
      if (settings.text_main) root.style.setProperty('--text-main', settings.text_main);

      // Цвета фона
      if (settings.background_color) root.style.setProperty('--background-color', settings.background_color);
      if (settings.background_light) root.style.setProperty('--background-light', settings.background_light);
      if (settings.background_dark) root.style.setProperty('--background-dark', settings.background_dark);
      if (settings.background_hover) root.style.setProperty('--background-hover', settings.background_hover);

      // Цвета обводок/границ
      if (settings.border_color) root.style.setProperty('--border-color', settings.border_color);
      if (settings.border_light) root.style.setProperty('--border-light', settings.border_light);
      if (settings.border_dark) root.style.setProperty('--border-dark', settings.border_dark);
      if (settings.border_focus) root.style.setProperty('--border-focus', settings.border_focus);

      // Цвета состояний
      if (settings.active_color) root.style.setProperty('--active-color', settings.active_color);
      if (settings.hover_color) root.style.setProperty('--hover-color', settings.hover_color);
      if (settings.disabled_color) root.style.setProperty('--disabled-color', settings.disabled_color);
      if (settings.disabled_background) root.style.setProperty('--disabled-background', settings.disabled_background);

      // Цвета уведомлений
      if (settings.success_color) root.style.setProperty('--success-color', settings.success_color);
      if (settings.success_background) root.style.setProperty('--success-background', settings.success_background);
      if (settings.error_color) root.style.setProperty('--error-color', settings.error_color);
      if (settings.error_background) root.style.setProperty('--error-background', settings.error_background);
      if (settings.warning_color) root.style.setProperty('--warning-color', settings.warning_color);
      if (settings.warning_background) root.style.setProperty('--warning-background', settings.warning_background);
      if (settings.info_color) root.style.setProperty('--info-color', settings.info_color);
      if (settings.info_background) root.style.setProperty('--info-background', settings.info_background);

      // Цвета специальных элементов
      if (settings.link_color) root.style.setProperty('--link-color', settings.link_color);
      if (settings.link_hover_color) root.style.setProperty('--link-hover-color', settings.link_hover_color);
      if (settings.shadow_color) root.style.setProperty('--shadow-color', settings.shadow_color);

      // Цвета бургер-меню
      if (settings.burger_background) root.style.setProperty('--burger-background', settings.burger_background);
      if (settings.burger_color) root.style.setProperty('--burger-color', settings.burger_color);
      if (settings.burger_hover_background) root.style.setProperty('--burger-hover-background', settings.burger_hover_background);
      if (settings.burger_hover_color) root.style.setProperty('--burger-hover-color', settings.burger_hover_color);
      if (settings.burger_active_background) root.style.setProperty('--burger-active-background', settings.burger_active_background);
      if (settings.burger_active_color) root.style.setProperty('--burger-active-color', settings.burger_active_color);

      // Цвета полей ввода
      if (settings.input_background) root.style.setProperty('--input-background', settings.input_background);
      if (settings.input_text) root.style.setProperty('--input-text', settings.input_text);
      if (settings.input_placeholder) root.style.setProperty('--input-placeholder', settings.input_placeholder);
      if (settings.input_border) root.style.setProperty('--input-border', settings.input_border);
      if (settings.input_focus_border) root.style.setProperty('--input-focus-border', settings.input_focus_border);
      if (settings.input_focus_background) root.style.setProperty('--input-focus-background', settings.input_focus_background);

      // Логотип - динамически меняем src атрибут изображения
      let logoUrl = settings.logo_url || require('assets/icon/logo.png');

      // Если это путь к файлу, формируем полный URL
      if (logoUrl && logoUrl.startsWith('/files/')) {
        logoUrl = `${env.nodeHost}${logoUrl}`;
      }

      const logoImg = document.querySelector('.sidebarIcon img');
      if (logoImg) {
        logoImg.src = logoUrl;
      }
    } else {
      // Если нет активных настроек, сбрасываем к значениям по умолчанию
      root.style.removeProperty('--main-color');
      root.style.removeProperty('--main-color-border');
      root.style.removeProperty('--secondary-color');
      root.style.removeProperty('--text-color');
      root.style.removeProperty('--text-light-color');
      root.style.removeProperty('--text-dark-color');
      root.style.removeProperty('--text-on-primary');
      root.style.removeProperty('--text-active');
      root.style.removeProperty('--text-inactive');
      root.style.removeProperty('--text-sidebar');
      root.style.removeProperty('--text-footer');
      root.style.removeProperty('--text-header');
      root.style.removeProperty('--text-main');
      root.style.removeProperty('--background-color');
      root.style.removeProperty('--background-light');
      root.style.removeProperty('--background-dark');
      root.style.removeProperty('--background-hover');
      root.style.removeProperty('--border-color');
      root.style.removeProperty('--border-light');
      root.style.removeProperty('--border-dark');
      root.style.removeProperty('--border-focus');
      root.style.removeProperty('--active-color');
      root.style.removeProperty('--hover-color');
      root.style.removeProperty('--disabled-color');
      root.style.removeProperty('--disabled-background');
      root.style.removeProperty('--success-color');
      root.style.removeProperty('--success-background');
      root.style.removeProperty('--error-color');
      root.style.removeProperty('--error-background');
      root.style.removeProperty('--warning-color');
      root.style.removeProperty('--warning-background');
      root.style.removeProperty('--info-color');
      root.style.removeProperty('--info-background');
      root.style.removeProperty('--link-color');
      root.style.removeProperty('--link-hover-color');
      root.style.removeProperty('--shadow-color');
      root.style.removeProperty('--burger-background');
      root.style.removeProperty('--burger-color');
      root.style.removeProperty('--burger-hover-background');
      root.style.removeProperty('--burger-hover-color');
      root.style.removeProperty('--burger-active-background');
      root.style.removeProperty('--burger-active-color');
      root.style.removeProperty('--input-background');
      root.style.removeProperty('--input-text');
      root.style.removeProperty('--input-placeholder');
      root.style.removeProperty('--input-border');
      root.style.removeProperty('--input-focus-border');
      root.style.removeProperty('--input-focus-background');
    }
  };

  const updateBrandingSettings = (newSettings) => {
    // Если есть путь к логотипу, формируем полный URL
    if (newSettings.logo_url && newSettings.logo_url.startsWith('/files/')) {
      newSettings.logo_url = `${env.nodeHost}${newSettings.logo_url}`;
    }

    setBrandingSettings(newSettings);
    applyBrandingSettings(newSettings);

    // Обновляем логотип при изменении настроек
    const logoImg = document.querySelector('.sidebarIcon img');
    if (logoImg) {
      let logoUrl = newSettings.logo_url || require('assets/icon/logo.png');

      // Если это путь к файлу, формируем полный URL
      if (logoUrl && logoUrl.startsWith('/files/')) {
        logoUrl = `${env.nodeHost}${logoUrl}`;
      }

      logoImg.src = logoUrl;
    }
  };

  const updateLogo = (logoUrl) => {
    const logoImg = document.querySelector('.sidebarIcon img');
    if (logoImg) {
      let finalLogoUrl = logoUrl || require('assets/icon/logo.png');

      // Если это путь к файлу, формируем полный URL
      if (finalLogoUrl && finalLogoUrl.startsWith('/files/')) {
        finalLogoUrl = `${env.nodeHost}${finalLogoUrl}`;
      }

      logoImg.src = finalLogoUrl;
    }
  };

  const value = {
    brandingSettings,
    updateBrandingSettings,
    updateLogo,
    isLoading
  };

  return (
    <BrandingContext.Provider value={value}>
      <BrandingLoader isLoading={isLoading}>
        {children}
      </BrandingLoader>
    </BrandingContext.Provider>
  );
};

export default BrandingProvider;
